import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useAuthRedirect } from "@shared";
import { signInSchema, getFieldSchema, type SignInForm } from "@shared";
import type { AuthErrorCode } from "@printtat/shared";
import { Form, FormField, Button, Flex, Container, Text, Separator, Alert, GoogleIcon } from "@printtat/ui";
import { Loader2 } from "lucide-react";
import { setAccessToken } from "@/lib/authToken";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000";

class AuthApiError extends Error {
  readonly code: AuthErrorCode | undefined;
  constructor(message: string, code: AuthErrorCode | undefined) {
    super(message);
    this.code = code;
  }
}

// Sign-in goes direct to fetch (not apiFetch) because:
//  - We need credentials: 'include' so the refresh cookie is stored.
//  - The response carries the access token which we hand to setAccessToken.
//  - There's no existing access token to attach.
const signInUser = async (data: SignInForm) => {
  const response = await fetch(`${API_URL}/auth/sign-in`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new AuthApiError(
      errorData.message || "Sign in failed",
      errorData.code,
    );
  }

  return response.json() as Promise<
    | { accessToken: string; user: { isVerified: boolean } }
    | { mfaRequired: true; challengeToken: string }
  >;
};

const resendVerificationEmail = async (email: string) => {
  const response = await fetch(`${API_URL}/auth/resend-verification`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to resend verification email");
  }
  return response.json();
};

// API function for Google OAuth
const initiateGoogleOAuth = () => {
  window.location.href = `${API_URL}/auth/google`;
};

export const SignInPage = () => {
  const { loading } = useAuthRedirect(); // This will redirect to dashboard if already authenticated
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [defaultEmail, setDefaultEmail] = useState<string>("");

  // Hydrate the "check your email" or LINK_REQUIRED banner from a redirect,
  // then clear the state so a refresh doesn't keep showing it.
  useEffect(() => {
    const state = location.state as
      | { info?: string; error?: string; prefillEmail?: string }
      | null;
    if (state?.info) setInfo(state.info);
    if (state?.error) setError(state.error);
    if (state?.prefillEmail) setDefaultEmail(state.prefillEmail);
    if (state?.info || state?.error || state?.prefillEmail) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const signInMutation = useMutation({
    mutationFn: signInUser,
    onSuccess: (data) => {
      setError(null);
      setInfo(null);
      setUnverifiedEmail(null);
      if ('mfaRequired' in data) {
        // Phase 4 — password verified but the account also requires a TOTP
        // factor. No access token has been issued yet; hand the challenge
        // token to the MFA step which exchanges it for a real session.
        navigate("/sign-in/mfa", {
          replace: true,
          state: { challengeToken: data.challengeToken },
        });
        return;
      }
      // Phase 2 auth-hardening: token lives in memory only. The refresh
      // cookie was set by the backend on the same response. A full-page
      // nav lets AuthProvider bootstrap fresh from the in-memory token.
      setAccessToken(data.accessToken);
      window.location.href = "/dashboard";
    },
    onError: (err: Error, variables) => {
      setInfo(null);
      setUnverifiedEmail(null);
      const code = err instanceof AuthApiError ? err.code : undefined;
      switch (code) {
        case "EMAIL_NOT_VERIFIED":
          setError(null);
          setUnverifiedEmail(variables.email);
          setInfo(
            "Your email isn't verified yet. Check your inbox for the verification link, or resend it below.",
          );
          return;
        case "INVALID_CREDENTIALS":
          setError("Invalid email or password, please try again.");
          return;
        default:
          setError(err.message || "Sign in failed. Please try again.");
      }
    },
  });

  const resendMutation = useMutation({
    mutationFn: resendVerificationEmail,
    onSuccess: () => {
      setInfo("Verification email sent. Please check your inbox.");
    },
    onError: (err: Error) => {
      setError(err.message || "Could not resend verification email.");
    },
  });

  const handleSubmit = async (data: SignInForm) => {
    signInMutation.mutate(data);
  };

  // Show loading while checking authentication
  if (loading) {
    return (
      <Container size="2" className="min-h-screen">
        <Flex align="center" justify="center" className="min-h-screen">
          <Flex direction="column" align="center" gap="4">
            <Loader2 className="h-12 w-12 animate-spin" />
            <Text size="3" color="gray">
              Checking authentication...
            </Text>
          </Flex>
        </Flex>
      </Container>
    );
  }

  return (
    <Container size="2" className="min-h-screen">
      <Flex align="center" justify="center" className="min-h-screen">
        <Flex direction="column" gap="6" className="w-full max-w-sm">
          <Flex direction="column" align="center" gap="2">
            <Text size="8" weight="bold" align="center">
              Sign in to your account
            </Text>
            <Text size="2" color="gray" align="center">
              No account? {" "}
              <Link
                to="/sign-up"
                className="text-blue-600 hover:text-blue-700 font-medium no-underline"
              >
                Sign up
              </Link>
            </Text>
          </Flex>

          <Form
            schema={signInSchema}
            defaultValues={{
              email: defaultEmail,
              password: "",
            }}
            onSubmit={handleSubmit}
            submitText="Sign In"
            loadingText="Signing in..."
          >
            {(form) => (
              <Flex direction="column" gap="4">
                <FormField
                  form={form}
                  name="email"
                  label="Email address"
                  type="email"
                  autoComplete="email"
                  required
                  placeholder="Email address"
                  validationSchema={getFieldSchema(signInSchema, "email")}
                />
                <Flex direction="column" gap="1">
                  <FormField
                    form={form}
                    name="password"
                    label="Password"
                    type="password"
                    autoComplete="current-password"
                    required
                    placeholder="Password"
                    validationSchema={getFieldSchema(signInSchema, "password")}
                  />
                  <Flex justify="end">
                    <Link
                      to="/forgot-password"
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium no-underline"
                    >
                      Forgot password?
                    </Link>
                  </Flex>
                </Flex>

                {info && <Alert variant="info">{info}</Alert>}
                {error && <Alert variant="error">{error}</Alert>}
                {unverifiedEmail && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => resendMutation.mutate(unverifiedEmail)}
                    disabled={resendMutation.isPending}
                  >
                    {resendMutation.isPending
                      ? "Sending..."
                      : "Resend verification email"}
                  </Button>
                )}
              </Flex>
            )}
          </Form>

          <Flex direction="column" gap="4">
            <Separator size="4" />

            <Flex align="center" justify="center">
              <Text size="2" color="gray">
                Alternative sign in methods
              </Text>
            </Flex>

            <Button
              variant="outline"
              onClick={initiateGoogleOAuth}
              className="w-full"
            >
              <GoogleIcon size={20} />
              <Text className="ml-2">Sign in with Google</Text>
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Container>
  );
};
